import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Save, X, RotateCcw, Hourglass, OctagonPause, Plus, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  saveRecordJustification,
  deleteRecordJustification,
  createAnomaly,
  deleteAnomaly,
  listAnomalies,
  listAnomalyReasons,
  createAnomalyReason,
  updateAnomalyReason,
  deleteAnomalyReason,
  listOperators,
  createOperator,
  updateOperator,
  deleteOperator,
} from "@/lib/api";

const FIELD_OPTIONS = [
  { key: "between_markers", label: "Entre marcadas", icon: Hourglass,    hint: "Tempo entre marcadas" },
  { key: "machine_stopped", label: "Intra marcadas", icon: OctagonPause, hint: "Tempo de paragem dentro da marcada" },
];

const SEVERITY = [
  { key: "low",    label: "Baixa",  cls: "border-emerald-300 text-emerald-700 dark:border-emerald-800 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40" },
  { key: "medium", label: "Média",  cls: "border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40" },
  { key: "high",   label: "Alta",   cls: "border-red-300 text-red-700 dark:border-red-800 dark:text-red-300 bg-red-50 dark:bg-red-950/40" },
];

function clampInt(v, min, max) {
  const n = parseInt(String(v ?? ""), 10);
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function minutesToParts(min) {
  if (!Number.isFinite(min) || min < 0) return { m: "", s: "" };
  const totalSec = Math.round(min * 60);
  return { m: String(Math.floor(totalSec / 60)), s: String(totalSec % 60).padStart(2, "0") };
}

function partsToMinutes(m, s) {
  const mm = clampInt(m === "" ? 0 : m, 0, 999);
  const ss = clampInt(s === "" ? 0 : s, 0, 59);
  return mm + ss / 60;
}

function fmtMMSS(min) {
  if (min === null || min === undefined) return "—";
  const n = Number(min);
  if (Number.isNaN(n)) return "—";
  const totalSec = Math.max(0, Math.round(n * 60));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function timeOnly(hms) { return hms ? hms.slice(0, 5) : "—"; }

export default function RecordJustificationModal({
  open,
  onOpenChange,
  date,
  record,
  onSaved,
}) {
  // Motivo + severidade
  const [reasonsList, setReasonsList] = useState([]); // [{id,label}]
  const [reason, setReason] = useState("");
  const [showAddReason, setShowAddReason] = useState(false);
  const [newReasonLabel, setNewReasonLabel] = useState("");
  const [managingReasons, setManagingReasons] = useState(false);
  const [editingReasonId, setEditingReasonId] = useState(null);
  const [editingReasonLabel, setEditingReasonLabel] = useState("");
  const [severity, setSeverity] = useState("medium");

  // Operator: managed list with select + inline add/edit
  const [operators, setOperators] = useState([]); // [{id,name}]
  const [operator, setOperator] = useState("");
  const [showAddOperator, setShowAddOperator] = useState(false);
  const [newOperatorName, setNewOperatorName] = useState("");
  const [managingOperators, setManagingOperators] = useState(false);
  const [editingOperatorId, setEditingOperatorId] = useState(null);
  const [editingOperatorName, setEditingOperatorName] = useState("");
  const [description, setDescription] = useState("");

  // Optional value override — ENABLED by default per operator workflow
  const [adjustEnabled, setAdjustEnabled] = useState(true);
  const [field, setField] = useState("between_markers");
  const [minPart, setMinPart] = useState("");
  const [secPart, setSecPart] = useState("");

  // Existing items detection
  const [existingAnomaly, setExistingAnomaly] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const existingJustifications = record?.justifications || {};
  const existingJustForField = existingJustifications[field] || null;

  const realValue = useMemo(() => {
    if (!record) return null;
    const v = record[field];
    return v === null || v === undefined ? 0 : Number(v);
  }, [record, field]);

  // Suggested value parsed back to decimal minutes
  const suggestedMin = useMemo(() => partsToMinutes(minPart, secPart), [minPart, secPart]);
  const delta = useMemo(() => {
    if (!adjustEnabled || realValue === null) return null;
    return suggestedMin - realValue;
  }, [adjustEnabled, suggestedMin, realValue]);

  // Fetch reasons (auto-seeded on backend if empty)
  const fetchReasons = useCallback(async () => {
    try {
      const list = await listAnomalyReasons();
      setReasonsList(list || []);
      // Initialize selected reason if none picked yet
      setReason((cur) => cur || (list?.[0]?.label || ""));
    } catch (_) { /* ignore */ }
  }, []);

  // Fetch operators
  const fetchOperators = useCallback(async () => {
    try {
      const list = await listOperators();
      setOperators(list || []);
    } catch (_) { /* ignore */ }
  }, []);

  // Fetch any existing operator-reported anomaly covering this row's time range
  const fetchExistingAnomaly = useCallback(async () => {
    if (!open || !record?.start_time || !date) return;
    try {
      const list = await listAnomalies({ date });
      const recStart = `${date}T${record.start_time}`;
      const recEnd = `${date}T${record.end_time || record.start_time}`;
      const match = (list || []).find((a) => {
        if (!a.start_at) return false;
        const aStart = String(a.start_at).slice(0, 19);
        const aEnd = String(a.end_at || a.start_at).slice(0, 19);
        return aStart <= recStart && aEnd >= recEnd;
      });
      setExistingAnomaly(match || null);
    } catch (_) {
      setExistingAnomaly(null);
    }
  }, [open, record?.start_time, record?.end_time, date]);

  useEffect(() => {
    if (!open) return;
    setField("between_markers");
    setAdjustEnabled(true);
    setMinPart("");
    setSecPart("");
    setSeverity("medium");
    setDescription("");
    setShowAddReason(false);
    setEditingReasonId(null);
    setManagingReasons(false);
    setShowAddOperator(false);
    setEditingOperatorId(null);
    setManagingOperators(false);
    fetchReasons();
    fetchOperators();
    fetchExistingAnomaly();
  }, [open, record?.start_time, fetchReasons, fetchOperators, fetchExistingAnomaly]);

  useEffect(() => {
    if (!existingAnomaly) return;
    setReason(existingAnomaly.reason || "");
    if (existingAnomaly.severity) setSeverity(existingAnomaly.severity);
    if (existingAnomaly.operator) setOperator(existingAnomaly.operator);
    if (existingAnomaly.description) setDescription(existingAnomaly.description);
  }, [existingAnomaly]);

  // Pre-fill the override input from an existing justification on the picked field
  useEffect(() => {
    if (!open) return;
    if (existingJustForField) {
      const { m, s } = minutesToParts(existingJustForField.suggested_value);
      setMinPart(m);
      setSecPart(s);
      setAdjustEnabled(true);
    } else {
      setMinPart("");
      setSecPart("");
    }
  }, [open, field, existingJustForField]);

  if (!record) return null;

  async function onAddReason() {
    const label = newReasonLabel.trim();
    if (!label) return;
    try {
      const doc = await createAnomalyReason(label);
      toast.success("Motivo criado");
      setNewReasonLabel("");
      setShowAddReason(false);
      await fetchReasons();
      setReason(doc.label);
    } catch (e) {
      toast.error(`Falha: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function onRenameReason() {
    const newLabel = editingReasonLabel.trim();
    if (!newLabel || !editingReasonId) return;
    const old = reasonsList.find((r) => r.id === editingReasonId);
    try {
      await updateAnomalyReason(editingReasonId, newLabel);
      toast.success("Motivo actualizado");
      setEditingReasonId(null);
      setEditingReasonLabel("");
      await fetchReasons();
      if (old && reason === old.label) setReason(newLabel);
    } catch (e) {
      toast.error(`Falha: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function onDeleteReason(id, label) {
    if (!confirm(`Apagar o motivo "${label}"?`)) return;
    try {
      await deleteAnomalyReason(id);
      await fetchReasons();
      if (reason === label) setReason("");
    } catch (e) {
      toast.error(`Falha: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function onAddOperator() {
    const name = newOperatorName.trim();
    if (!name) return;
    try {
      const doc = await createOperator(name);
      toast.success("Operador criado");
      setNewOperatorName("");
      setShowAddOperator(false);
      await fetchOperators();
      setOperator(doc.name);
    } catch (e) {
      toast.error(`Falha: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function onRenameOperator() {
    const newName = editingOperatorName.trim();
    if (!newName || !editingOperatorId) return;
    const old = operators.find((o) => o.id === editingOperatorId);
    try {
      await updateOperator(editingOperatorId, { name: newName });
      toast.success("Operador actualizado");
      setEditingOperatorId(null);
      setEditingOperatorName("");
      await fetchOperators();
      if (old && operator === old.name) setOperator(newName);
    } catch (e) {
      toast.error(`Falha: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function onDeleteOperator(id, name) {
    if (!confirm(`Apagar o operador "${name}"?`)) return;
    try {
      await deleteOperator(id);
      await fetchOperators();
      if (operator === name) setOperator("");
    } catch (e) {
      toast.error(`Falha: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function onSave() {
    if (!reason.trim()) { toast.error("Indica o motivo"); return; }
    setSaving(true);
    try {
      const start_at = `${date}T${record.start_time}`;
      const end_at = record.end_time ? `${date}T${record.end_time}` : null;
      if (existingAnomaly) {
        await deleteAnomaly(existingAnomaly.id);
      }
      await createAnomaly({
        date,
        start_at,
        end_at,
        reason: reason.trim(),
        description: description.trim() || null,
        operator: operator.trim() || null,
        severity,
      });
      if (adjustEnabled && suggestedMin >= 0) {
        await saveRecordJustification({
          date,
          record_key: record.start_time,
          field,
          real_value: realValue || 0,
          suggested_value: suggestedMin,
          reason: reason.trim(),
          description: description.trim() || null,
          operator: operator.trim() || null,
        });
      }
      toast.success(existingAnomaly ? "Anomalia actualizada" : "Anomalia registada");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(`Falha: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function onRemove() {
    if (!existingAnomaly && Object.keys(existingJustifications).length === 0) return;
    if (!confirm("Remover anomalia e quaisquer ajustes de valor desta linha?")) return;
    setDeleting(true);
    try {
      if (existingAnomaly) await deleteAnomaly(existingAnomaly.id);
      for (const j of Object.values(existingJustifications)) {
        if (j?.id) await deleteRecordJustification(j.id);
      }
      toast.success("Linha reposta para Real");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(`Falha: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDeleting(false);
    }
  }

  const hasExisting = !!existingAnomaly || Object.keys(existingJustifications).length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="record-justification-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            Justificar / reportar anomalia
            {hasExisting && (
              <Badge variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-300 text-[10px]">
                Existente
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* 1. Time range — read-only */}
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Período da linha</div>
            <div className="text-lg font-semibold tabular-nums">
              {timeOnly(record.start_time)} – {timeOnly(record.end_time)}
            </div>
            <div className="text-[10px] text-muted-foreground">
              Registado para o dia <span className="tabular-nums">{date}</span>.
              Esta linha será <span className="font-semibold text-foreground">excluída</span> do total <em>Ajustado</em>.
            </div>
          </div>

          {/* 2. (Optional) Adjust value — BEFORE motivo */}
          <div className="rounded-md border border-border">
            <label className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none border-b border-border bg-muted/30">
              <input
                type="checkbox"
                checked={adjustEnabled}
                onChange={(e) => setAdjustEnabled(e.target.checked)}
                className="h-4 w-4"
                data-testid="toggle-advanced"
              />
              <span className="text-xs font-medium">
                Ajustar também um valor capturado
              </span>
              <span className="text-[10px] text-muted-foreground hidden sm:inline">
                · Substitui o valor real em vez de excluir a linha
              </span>
            </label>
            {adjustEnabled && (
              <div className="p-3 space-y-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Campo a ajustar</label>
                  <div className="grid grid-cols-3 gap-1.5 mt-1">
                    {FIELD_OPTIONS.map((opt) => {
                      const Ico = opt.icon;
                      const has = !!existingJustifications?.[opt.key];
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setField(opt.key)}
                          data-testid={`field-${opt.key}`}
                          className={`text-xs px-2 py-2 rounded-md border transition-colors flex flex-col items-center gap-1 relative ${
                            field === opt.key
                              ? "bg-primary text-primary-foreground border-transparent"
                              : "bg-card border-border text-foreground hover:bg-muted"
                          }`}
                          title={opt.hint}
                        >
                          <Ico className="h-3.5 w-3.5" />
                          <span className="text-[10px] leading-tight text-center">{opt.label}</span>
                          {has && <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-amber-400" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Real (capturado)</div>
                    <div className="text-lg font-semibold tabular-nums">{fmtMMSS(realValue)}</div>
                  </div>
                  <div className="rounded-md border border-amber-300 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/30 px-3 py-2">
                    <label className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400">Sugerido</label>
                    <div className="flex items-baseline gap-1 mt-1">
                      <Input
                        type="number"
                        min="0"
                        max="999"
                        value={minPart}
                        onChange={(e) => setMinPart(e.target.value.replace(/\D/g, ""))}
                        placeholder="0"
                        className="h-8 text-lg font-semibold tabular-nums px-1 w-14 text-center border bg-white dark:bg-amber-950/30"
                        data-testid="justification-suggested-min-input"
                      />
                      <span className="text-lg font-semibold text-muted-foreground">:</span>
                      <Input
                        type="number"
                        min="0"
                        max="59"
                        value={secPart}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, "").slice(0, 2);
                          setSecPart(v);
                        }}
                        onBlur={() => setSecPart((v) => (v === "" ? "" : String(clampInt(v, 0, 59)).padStart(2, "0")))}
                        placeholder="00"
                        className="h-8 text-lg font-semibold tabular-nums px-1 w-14 text-center border bg-white dark:bg-amber-950/30"
                        data-testid="justification-suggested-sec-input"
                      />
                      <span className="text-[10px] text-muted-foreground ml-1">min : seg</span>
                    </div>
                    {delta !== null && (
                      <div className={`text-[10px] tabular-nums mt-1 ${delta > 0 ? 'text-red-600' : delta < 0 ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                        {delta > 0 ? '+' : ''}{delta.toFixed(2)} min vs real
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 3. Motivo */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Motivo</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { setManagingReasons((v) => !v); setEditingReasonId(null); }}
                  className={`text-[11px] inline-flex items-center gap-1 hover:underline ${managingReasons ? 'text-amber-600' : 'text-muted-foreground'}`}
                  data-testid="anomaly-manage-reasons-btn"
                >
                  <Pencil className="h-3 w-3" /> {managingReasons ? 'Concluir' : 'Gerir'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddReason((v) => !v)}
                  className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
                  data-testid="anomaly-add-reason-btn"
                >
                  <Plus className="h-3 w-3" /> Novo motivo
                </button>
              </div>
            </div>
            {showAddReason && (
              <div className="flex gap-2 mb-2 p-2 rounded-md bg-muted/40 border border-border">
                <Input
                  placeholder="Nome do motivo (ex: Manutenção do tipo X)"
                  value={newReasonLabel}
                  onChange={(e) => setNewReasonLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAddReason(); } }}
                  className="h-8 text-xs"
                  data-testid="anomaly-new-reason-input"
                  autoFocus
                />
                <Button type="button" size="sm" onClick={onAddReason} data-testid="anomaly-create-reason-btn">
                  Criar
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => { setShowAddReason(false); setNewReasonLabel(""); }}>
                  Cancelar
                </Button>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {reasonsList.map((r) => {
                const isEditing = editingReasonId === r.id;
                if (isEditing) {
                  return (
                    <div key={r.id} className="col-span-2 sm:col-span-3 flex gap-1 p-1 rounded-md bg-muted/40 border border-border">
                      <Input
                        value={editingReasonLabel}
                        onChange={(e) => setEditingReasonLabel(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onRenameReason(); } }}
                        className="h-7 text-xs"
                        autoFocus
                      />
                      <Button type="button" size="sm" onClick={onRenameReason} className="h-7"><Check className="h-3.5 w-3.5" /></Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => { setEditingReasonId(null); setEditingReasonLabel(""); }} className="h-7"><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  );
                }
                if (managingReasons) {
                  // Manage mode: chip is split into Rename + Delete actions
                  return (
                    <div
                      key={r.id}
                      className="flex items-stretch rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-950/20 overflow-hidden"
                      data-testid={`reason-manage-${r.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <button
                        type="button"
                        onClick={() => { setEditingReasonId(r.id); setEditingReasonLabel(r.label); }}
                        className="flex-1 text-xs px-2 py-1.5 text-left truncate hover:bg-amber-100 dark:hover:bg-amber-900/40 inline-flex items-center gap-1.5"
                        title="Renomear"
                      >
                        <Pencil className="h-3 w-3 text-amber-700 dark:text-amber-300 shrink-0" />
                        <span className="truncate">{r.label}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => onDeleteReason(r.id, r.label)}
                        className="px-2 border-l border-amber-300 dark:border-amber-700 hover:bg-red-500 hover:text-white text-red-600 dark:text-red-400 transition-colors"
                        title="Apagar"
                        data-testid={`reason-delete-${r.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                }
                // Normal mode: clean select chip
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setReason(r.label)}
                    data-testid={`reason-${r.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`w-full text-xs px-2 py-1.5 rounded-md border ${
                      reason === r.label
                        ? "bg-primary text-primary-foreground border-transparent"
                        : "bg-card border-border text-foreground hover:bg-muted"
                    }`}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 4. Gravidade */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Gravidade</label>
            <div className="grid grid-cols-3 gap-1.5 mt-1">
              {SEVERITY.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSeverity(s.key)}
                  data-testid={`severity-${s.key}`}
                  className={`text-xs px-2 py-1.5 rounded-md border transition-colors ${
                    severity === s.key
                      ? s.cls
                      : "bg-card border-border text-foreground hover:bg-muted"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* 5. Operador (managed list) + notas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Operador</label>
                <div className="flex items-center gap-2">
                  {operators.length > 0 && (
                    <button
                      type="button"
                      onClick={() => { setManagingOperators((v) => !v); setEditingOperatorId(null); }}
                      className={`text-[11px] inline-flex items-center gap-1 hover:underline ${managingOperators ? 'text-amber-600' : 'text-muted-foreground'}`}
                      data-testid="anomaly-manage-operators-btn"
                    >
                      <Pencil className="h-3 w-3" /> {managingOperators ? 'Concluir' : 'Gerir'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowAddOperator((v) => !v)}
                    className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
                    data-testid="anomaly-add-operator-btn"
                  >
                    <Plus className="h-3 w-3" /> Novo
                  </button>
                </div>
              </div>
              {showAddOperator && (
                <div className="flex gap-1 mb-2 p-1.5 rounded-md bg-muted/40 border border-border">
                  <Input
                    placeholder="Nome do operador"
                    value={newOperatorName}
                    onChange={(e) => setNewOperatorName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAddOperator(); } }}
                    className="h-7 text-xs"
                    data-testid="anomaly-new-operator-input"
                    autoFocus
                  />
                  <Button type="button" size="sm" onClick={onAddOperator} className="h-7" data-testid="anomaly-create-operator-btn">
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => { setShowAddOperator(false); setNewOperatorName(""); }} className="h-7">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
              {operators.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {operators.map((o) => {
                    const isEditing = editingOperatorId === o.id;
                    if (isEditing) {
                      return (
                        <div key={o.id} className="flex gap-1 p-1 rounded-md bg-muted/40 border border-border w-full">
                          <Input
                            value={editingOperatorName}
                            onChange={(e) => setEditingOperatorName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onRenameOperator(); } }}
                            className="h-7 text-xs"
                            autoFocus
                          />
                          <Button type="button" size="sm" onClick={onRenameOperator} className="h-7"><Check className="h-3.5 w-3.5" /></Button>
                          <Button type="button" size="sm" variant="ghost" onClick={() => { setEditingOperatorId(null); setEditingOperatorName(""); }} className="h-7"><X className="h-3.5 w-3.5" /></Button>
                        </div>
                      );
                    }
                    if (managingOperators) {
                      return (
                        <div
                          key={o.id}
                          className="flex items-stretch rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-950/20 overflow-hidden"
                          data-testid={`operator-manage-${o.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <button
                            type="button"
                            onClick={() => { setEditingOperatorId(o.id); setEditingOperatorName(o.name); }}
                            className="text-xs px-2.5 py-1.5 inline-flex items-center gap-1.5 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                            title="Renomear"
                          >
                            <Pencil className="h-3 w-3 text-amber-700 dark:text-amber-300" />
                            {o.name}
                          </button>
                          <button
                            type="button"
                            onClick={() => onDeleteOperator(o.id, o.name)}
                            className="px-2 border-l border-amber-300 dark:border-amber-700 hover:bg-red-500 hover:text-white text-red-600 dark:text-red-400 transition-colors"
                            title="Apagar"
                            data-testid={`operator-delete-${o.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      );
                    }
                    return (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => setOperator(o.name)}
                        data-testid={`operator-${o.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`text-xs px-2.5 py-1.5 rounded-md border ${
                          operator === o.name
                            ? "bg-primary text-primary-foreground border-transparent"
                            : "bg-card border-border text-foreground hover:bg-muted"
                        }`}
                      >
                        {o.name}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-[10px] text-muted-foreground italic">
                  Sem operadores. Clica em "+ Novo" para criar o primeiro.
                </div>
              )}
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Notas</label>
              <Input
                placeholder="Opcional"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="justification-description-input"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
            <div>
              {hasExisting && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onRemove}
                  disabled={deleting}
                  className="text-destructive hover:text-destructive"
                  data-testid="justification-remove-btn"
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Repor real
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                <X className="h-4 w-4 mr-1" /> Cancelar
              </Button>
              <Button type="button" size="sm" onClick={onSave} disabled={saving} data-testid="justification-save-btn">
                <Save className="h-4 w-4 mr-1" /> {saving ? "A guardar…" : (hasExisting ? "Actualizar" : "Registar")}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
